import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Grid, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import InfoIcon from '@material-ui/icons/Info';
import { CommonDataGrid } from 'features/shared/components/table';
import RMSCommonDialog from '../../../shared/components/dialog/RMSCommonDialog';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import AddressPartyDialog from './AddressPartyDialog';
import { getPersonActiveWarrants } from '../../services/userService';
import { getAddressParties, deleteAddressParty } from '../../services/addressService';
import {
  PARTY_GRID_COLUMNS,
  DETAIL_FIELDS,
  WARRANT_FIELDS,
} from '../../constants/addressPartiesTabConstants';
import { PARTY_KIND } from '../../constants/partyKindLookupConstants';
import { getPartyName, getPartyID } from '../../utility/partiesHelper';
import LocalPrintshopIcon from '@material-ui/icons/LocalPrintshop';
import { buildDetailPrintHTML } from '../../utility/detailsPrintHTMLHelper';
import FormSpinner from 'components/FormSpinner';

const AddressPartiesTab = ({ ptsAddressID }) => {
  const dispatch = useDispatch();
  const canEditParties = usePermissions(PERMISSION_RESOURCES.ADDRESS).Edit;
  const [loading, setLoading] = useState(false);
  const [parties, setParties] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingParty, setEditingParty] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [warrantTarget, setWarrantTarget] = useState(null);
  const [loadingWarrants, setLoadingWarrants] = useState(false);
  const printIframeRef = useRef(null);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();

  const processData = (data) => {
    return data.map((row) => {
      return {
        ...row,
        Name: getPartyName(row),
        PartyID: getPartyID(row),
        Relationship: row.AddressPartyRelationship,
      };
    });
  };

  const loadParties = useCallback(async () => {
    if (!ptsAddressID) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getAddressParties(ptsAddressID),
      onSuccess: (data) => setParties(processData(data || [])),
    });
  }, [dispatch, ptsAddressID]);

  const handleAdd = useCallback(() => {
    setEditingParty(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingParty(row);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingParty(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadParties();
  }, [handleFormClose, loadParties]);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to delete this "${row.Name || 'party'}" record?`,
        async () => {
          await executeApiAction({
            setLoading,
            dispatch,
            action: () => deleteAddressParty(row.ROWGUID),
            onSuccess: () => {
              dispatch(notify('Party deleted successfully', 'success'));
              loadParties();
            },
          });
        },
        'Delete Party'
      );
    },
    [dispatch, requestConfirm, loadParties]
  );

  const handleViewDetails = useCallback((row) => setDetailTarget(row), []);
  const handleCloseDetails = useCallback(() => setDetailTarget(null), []);
  const handleCloseWarrants = useCallback(() => setWarrantTarget(null), []);

  const handleViewWarrants = useCallback(
    async (row) => {
      if (!row.ActiveWarrant || row.PartyType !== PARTY_KIND.PERSON) return;

      await executeApiAction({
        setLoading: setLoadingWarrants,
        dispatch,
        action: () => getPersonActiveWarrants(row.ptsPartyID),
        onSuccess: (data) => setWarrantTarget({ name: row.Name, warrants: data || [] }),
      });
    },
    [dispatch]
  );

  const getRowClassName = useCallback(
    (params) => (params.row.ActiveWarrant ? 'address-parties-tab__warrant-row' : ''),
    []
  );

  const handlePrint = useCallback(() => {
    if (!detailTarget) return;

    const html = buildDetailPrintHTML(detailTarget, DETAIL_FIELDS, detailTarget.Name);
    const win = printIframeRef.current.contentWindow;

    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    win.print();
  }, [detailTarget]);

  useEffect(() => {
    loadParties();
  }, [loadParties]);

  return (
    <Box className="person-names-tab__grid-wrap address-parties-tab__grid-wrap">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Party"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditParties}
        />
      </Box>

      <CommonDataGrid
        rows={parties}
        columns={PARTY_GRID_COLUMNS}
        getRowId={(row) => row.ROWGUID}
        getRowClassName={getRowClassName}
        toolbar={getDataGridToolbarProps({ printTitle: 'Address Parties' })}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditParties },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditParties },
          custom: [
            {
              icon: <InfoIcon />,
              tooltip: 'View Details',
              onClick: handleViewDetails,
            },
          ],
        }}
      />

      {formOpen && (
        <AddressPartyDialog
          open={formOpen}
          ptsAddressID={ptsAddressID}
          party={editingParty}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {detailTarget && (
        <RMSCommonDialog
          open={Boolean(detailTarget)}
          title={detailTarget.Name || 'Party Details'}
          onClose={handleCloseDetails}
          maxWidth="sm"
          fullWidth
          saveButton={{ show: false }}
          cancelButton={{
            show: true,
            type: 'simple',
            label: 'Close',
            variant: 'outlined',
            color: 'secondary',
          }}
          customButtons={
            <RMSButton
              label="Print"
              variant="contained"
              color="primary"
              prefixIcon={<LocalPrintshopIcon />}
              onClick={handlePrint}
            />
          }>
          <Grid container spacing={1}>
            {DETAIL_FIELDS.map(({ field, label, render }) => (
              <Grid item xs={6} key={field}>
                <Typography variant="caption" color="textSecondary" component="div">
                  {label}
                </Typography>
                <Typography variant="body2">
                  {render ? render(detailTarget[field]) : detailTarget[field] || ''}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {detailTarget.ActiveWarrant && detailTarget.PartyType === PARTY_KIND.PERSON && (
            <Box mt={2}>
              <RMSButton
                label="View Active Warrants"
                variant="outlined"
                color="secondary"
                disabled={loadingWarrants}
                onClick={() => handleViewWarrants(detailTarget)}
              />
            </Box>
          )}
          <iframe
            ref={printIframeRef}
            title="address-party-print-frame"
            className="address-party-print-iframe"
          />
        </RMSCommonDialog>
      )}

      {warrantTarget && (
        <RMSCommonDialog
          open={Boolean(warrantTarget)}
          title={`Active Warrants for ${warrantTarget.name}`}
          onClose={handleCloseWarrants}
          maxWidth="sm"
          fullWidth
          saveButton={{ show: false }}
          cancelButton={{
            show: true,
            type: 'simple',
            label: 'Close',
            variant: 'outlined',
            color: 'secondary',
          }}>
          {warrantTarget.warrants.length === 0 && (
            <Typography variant="body2">No active warrants found.</Typography>
          )}
          {warrantTarget.warrants.map((warrant, index) => (
            <Box key={index} mb={2} pb={2} className="rms-dialog-section">
              <Grid container spacing={1}>
                {WARRANT_FIELDS.map(({ field, label, render }) => (
                  <Grid item xs={6} key={field}>
                    <Typography variant="caption" color="textSecondary" component="div">
                      {label}
                    </Typography>
                    <Typography variant="body2">
                      {render ? render(warrant[field]) : warrant[field] || ''}
                    </Typography>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
        </RMSCommonDialog>
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default AddressPartiesTab;
